import { ACTION, NUMBER, OBJECT } from '../../../utils/component-validations';
import {
  ACTION_ARGS,
  getDefaultValueFromMetadata,
  pickArgs,
  sortMetadata,
} from '../../../utils/spds/metadata';
import { and } from 'ember-truth-helpers';
import AlertBannerContainer from 'ember-simplepractice/components/spds/feedback/alert-banner/container';
import ImageCrop from 'ember-simplepractice/components/shared/image-crop';
import SpdsModal from 'ember-simplepractice/components/spds/modal/standard';
import Super from '../core/base.gjs';
import ToastContainer from 'ember-simplepractice/components/spds/feedback/toast/container';
import styles from './image-crop.module.scss';

const METADATA = sortMetadata({
  component: 'Spds::Modal::ImageCrop',
  name: 'Image Crop',
  description: 'A modal that allows cropping of profile images',
  meta: {
    ...pickArgs(ACTION_ARGS, ['onClose', 'onBeforeClose']).meta,
    onConfirm: {
      description: 'Action to be called when the image is confirmed',
      type: ACTION,
    },
    onClose: {
      description: 'Action to be called when the modal is closed',
      type: ACTION,
    },
    file: {
      description: 'The image file to be cropped',
      type: OBJECT,
    },
    minImgHeight: {
      description: 'Required minimum height for the image',
      type: NUMBER,
    },
    minImgWidth: {
      description: 'Required minimum width for the image',
      type: NUMBER,
    },
    recommendedMinImgHeight: {
      description: 'Recommended minimum height for the image',
      type: NUMBER,
    },
    recommendedMinImgWidth: {
      description: 'Recommended minimum width for the image',
      type: NUMBER,
    },
    viewportBaseSize: {
      description: 'The base size for the viewport',
      defaultValue: 200,
      type: NUMBER,
    },
    viewportWidthRatio: {
      description: 'The ratio of the viewport width',
      defaultValue: 1,
      type: NUMBER,
    },
    viewportHeightRatio: {
      description: 'The ratio of the viewport height',
      defaultValue: 1,
      type: NUMBER,
    },
  },
  resources: {
    github:
      'https://github.com/simplepractice/ember-simplepractice/blob/staging/addon/src/components/spds/modal/image-crop.gjs',
  },
});

export default class SpdsModalImageCrop extends Super {
  static metadata = METADATA;
  baseClass = 'spds-modal-image-crop';
  styles = styles;

  get bannerContainerId() {
    return this.composeSubcomponentId('banner-container');
  }

  get viewportBaseSize() {
    return (
      this.args.viewportBaseSize ??
      getDefaultValueFromMetadata(this.constructor.metadata, 'viewportBaseSize')
    );
  }

  get viewportHeightRatio() {
    return (
      this.args.viewportHeightRatio ??
      getDefaultValueFromMetadata(this.constructor.metadata, 'viewportHeightRatio')
    );
  }

  get viewportWidthRatio() {
    return (
      this.args.viewportWidthRatio ??
      getDefaultValueFromMetadata(this.constructor.metadata, 'viewportWidthRatio')
    );
  }

  <template>
    {{#if @file}}
      <SpdsModal
        @id={{this.id}}
        @onClose={{@onClose}}
        @onBeforeClose={{@onBeforeClose}}
        @closeOnClickOutside={{@closeOnClickOutside}}
        @alignItems="center"
        class={{this.classes}}
        data-test-spds-modal-image-crop
        as |M|
      >
        <M.Header @title="Edit image" />
        <M.Body>
          <AlertBannerContainer @id={{this.bannerContainerId}} />
          <ImageCrop
            @file={{@file}}
            @isStrictRecommendation={{@isStrictRecommendation}}
            @minImgHeight={{@minImgHeight}}
            @minImgWidth={{@minImgWidth}}
            @onClose={{@onClose}}
            @onConfirm={{@onConfirm}}
            @recommendedMinImgHeight={{@recommendedMinImgHeight}}
            @recommendedMinImgWidth={{@recommendedMinImgWidth}}
            @viewportBaseSize={{this.viewportBaseSize}}
            @viewportHeightRatio={{this.viewportHeightRatio}}
            @viewportWidthRatio={{this.viewportWidthRatio}}
            as |ic|
          >
            <ic.banner />
            {{#if (and ic.imgWidth ic.imgHeight)}}
              <ic.preview />
              {{#if ic.isAllowedToZoom}}
                <ic.range-input />
              {{/if}}
            {{/if}}
            <ic.actions @excludePwStyle={{@excludePwStyle}} />
          </ImageCrop>
          <ToastContainer @local={{true}} />
        </M.Body>
      </SpdsModal>
    {{/if}}
  </template>
}

export const IMAGE_CROP_METADATA = SpdsModalImageCrop.metadata;
